import { useEffect, useRef, useState } from "react";

type Api = <T>(
  path: string,
  options?: {
    method?: string;
    body?: unknown;
    csrf?: string | undefined;
    signal?: AbortSignal;
  },
) => Promise<T>;
type Check = {
  id: string;
  numberId: string;
  number: string;
  status: "matched" | "mismatch" | "unassigned";
  checkedAt: string;
  recordedOrder: string;
  current: boolean;
  locationVersion: number;
  observedAddress?: {
    line1: string;
    line2: string;
    city: string;
    region: string;
    postalCode: string;
  };
};
type Data = {
  numbers: { id: string; number: string; supported: boolean }[];
  checks: Check[];
};
const badResponse = () =>
  new Error(
    "Emergency check details could not be read. Reload the checks and try again.",
  );
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function object(value: unknown): Record<string, any> {
  if (!value || typeof value !== "object" || Array.isArray(value))
    throw badResponse();
  return value as Record<string, any>;
}
function decodeCheck(raw: unknown): Check {
  const row = object(raw);
  if (
    typeof row.id !== "string" ||
    !uuid.test(row.id) ||
    typeof row.numberId !== "string" ||
    !uuid.test(row.numberId) ||
    typeof row.number !== "string" ||
    !/^\+[1-9]\d{7,14}$/.test(row.number) ||
    !["matched", "mismatch", "unassigned"].includes(row.status) ||
    typeof row.current !== "boolean" ||
    !Number.isSafeInteger(row.locationVersion) ||
    row.locationVersion < 1 ||
    typeof row.checkedAt !== "string" ||
    !Number.isFinite(Date.parse(row.checkedAt)) ||
    typeof row.recordedOrder !== "string" ||
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{6}Z:[0-9a-f-]{36}$/.test(
      row.recordedOrder,
    ) ||
    row.phoneSystemVerified !== false ||
    row.evidenceKind !== "carrier_association"
  )
    throw badResponse();
  let observedAddress: Check["observedAddress"];
  if (row.observedAddress != null) {
    const address = object(row.observedAddress);
    if (
      !["line1", "line2", "city", "region", "postalCode"].every(
        (key) => typeof address[key] === "string" && address[key].length <= 300,
      )
    )
      throw badResponse();
    observedAddress = {
      line1: address.line1,
      line2: address.line2,
      city: address.city,
      region: address.region,
      postalCode: address.postalCode,
    };
  }
  return {
    id: row.id,
    numberId: row.numberId,
    number: row.number,
    status: row.status,
    current: row.current,
    locationVersion: row.locationVersion,
    checkedAt: row.checkedAt,
    recordedOrder: row.recordedOrder,
    ...(observedAddress ? { observedAddress } : {}),
  };
}
export function decodeEmergencyData(raw: unknown): Data {
  const value = object(raw);
  if (!Array.isArray(value.numbers) || !Array.isArray(value.checks))
    throw badResponse();
  const numbers = value.numbers.map((entry: unknown) => {
    const number = object(entry);
    if (
      typeof number.id !== "string" ||
      !uuid.test(number.id) ||
      typeof number.number !== "string" ||
      !/^\+[1-9]\d{7,14}$/.test(number.number) ||
      typeof number.supported !== "boolean"
    )
      throw badResponse();
    return {
      id: number.id as string,
      number: number.number as string,
      supported: number.supported as boolean,
    };
  });
  const checks = value.checks.map(decodeCheck);
  if (
    new Set(numbers.map((number) => number.id)).size !== numbers.length ||
    new Set(checks.map((check) => check.numberId)).size !== checks.length ||
    checks.some(
      (check) =>
        !numbers.some(
          (number) =>
            number.id === check.numberId && number.number === check.number,
        ),
    )
  )
    throw badResponse();
  return { numbers, checks };
}
export function decodeEmergencyResult(
  raw: unknown,
  numberId: string,
  number: string,
): Check {
  const check = decodeCheck(object(raw).latest);
  if (check.numberId !== numberId || check.number !== number)
    throw badResponse();
  return check;
}
export function mergeEmergencyCheck(data: Data, incoming: Check): Data {
  const current = data.checks.find(
    (check) => check.numberId === incoming.numberId,
  );
  if (current && current.recordedOrder > incoming.recordedOrder) return data;
  return {
    ...data,
    checks: [
      ...data.checks.filter((check) => check.numberId !== incoming.numberId),
      incoming,
    ],
  };
}
export function emergencyCheckLabel(
  check: Pick<Check, "current" | "status" | "locationVersion">,
  version: number,
) {
  if (!check.current || check.locationVersion !== version)
    return "Address or number changed. Check again.";
  if (check.status === "matched")
    return "Carrier emergency address matches the saved location.";
  if (check.status === "unassigned")
    return "No registered emergency address was verified at Twilio.";
  return "Carrier emergency address does not match this location.";
}

export function EmergencyCheckPanel(props: {
  api: Api;
  csrf?: string | undefined;
  accountId: string;
  customer: boolean;
  readOnly?: boolean | undefined;
  location: Record<string, any>;
}) {
  const { api, accountId, customer, location } = props;
  const path = `/${customer ? "customer" : "admin"}/accounts/${accountId}/locations/${location.id}/emergency-checks`;
  const [data, setData] = useState<Data | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState("");
  const [reload, setReload] = useState(0);
  const request = useRef<AbortController | null>(null);
  const inFlight = useRef(false);
  const attempt = useRef({ payload: "", key: crypto.randomUUID() });
  useEffect(() => {
    const controller = new AbortController();
    request.current?.abort();
    request.current = controller;
    setData(null);
    setLoading(true);
    setError("");
    setBusy(false);
    inFlight.current = false;
    api<unknown>(path, { signal: controller.signal })
      .then((raw) => {
        if (controller.signal.aborted) return;
        const value = decodeEmergencyData(raw);
        setData(value);
        setSelected((previous) =>
          value.numbers.some(
            (number) => number.id === previous && number.supported,
          )
            ? previous
            : (value.numbers.find((number) => number.supported)?.id ?? ""),
        );
      })
      .catch((cause) => {
        if (!controller.signal.aborted) setError((cause as Error).message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => {
      controller.abort();
      request.current?.abort();
    };
  }, [api, path, location.version, reload]);
  async function check() {
    if (!selected || props.readOnly || inFlight.current || loading) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    const body = {
      numberId: selected,
      expectedVersion: Number(location.version),
    };
    const payload = JSON.stringify(body);
    if (attempt.current.payload !== payload)
      attempt.current = { payload, key: crypto.randomUUID() };
    try {
      const raw = await api<unknown>(path, {
        method: "POST",
        body: { ...body, requestKey: attempt.current.key },
        csrf: props.csrf,
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      const result = decodeEmergencyResult(
        raw,
        selected,
        data?.numbers.find((number) => number.id === selected)?.number ?? "",
      );
      setData((previous) =>
        previous ? mergeEmergencyCheck(previous, result) : previous,
      );
      attempt.current = { payload: "", key: crypto.randomUUID() };
    } catch (cause) {
      if (!controller.signal.aborted) setError((cause as Error).message);
    } finally {
      if (!controller.signal.aborted) {
        setBusy(false);
        inFlight.current = false;
      }
    }
  }
  return (
    <details className="cr-address-check">
      <summary>Emergency service at the carrier</summary>
      <p>
        A fresh check reads the number's emergency address directly from Twilio.
        It does not change phone service.
      </p>
      {loading && <p role="status">Loading emergency address checks...</p>}
      {error && <p role="alert">{error}</p>}
      {!loading && !data && (
        <button
          className="cr-button"
          onClick={() => setReload((value) => value + 1)}
        >
          Reload emergency checks
        </button>
      )}
      {data && (
        <>
          {!data.numbers.length && (
            <p>
              Add an owned telephone number to this account before checking its
              emergency address.
            </p>
          )}
          {!!data.numbers.length &&
            !data.numbers.some((number) => number.supported) && (
              <p>
                Link an owned Twilio number to this account before checking its
                emergency address.
              </p>
            )}
          {!props.readOnly &&
            data.numbers.some((number) => number.supported) && (
              <div className="cr-form">
                <label>
                  Telephone number
                  <select
                    value={selected}
                    disabled={busy}
                    onChange={(event) => setSelected(event.target.value)}
                  >
                    {data.numbers
                      .filter((number) => number.supported)
                      .map((number) => (
                        <option key={number.id} value={number.id}>
                          {number.number}
                        </option>
                      ))}
                  </select>
                </label>
                <button
                  className="cr-button"
                  type="button"
                  disabled={busy || !selected}
                  onClick={() => void check()}
                >
                  {busy ? "Checking Twilio..." : "Check emergency address"}
                </button>
              </div>
            )}
          <div aria-live="polite">
            {!data.checks.length && !!data.numbers.length && (
              <p>
                No carrier emergency address checks have been recorded for this
                location.
              </p>
            )}
            {data.checks.map((item) => (
              <div key={item.id}>
                <strong>{item.number}</strong>
                <p>{emergencyCheckLabel(item, Number(location.version))}</p>
                <p>Checked {new Date(item.checkedAt).toLocaleString()}</p>
                {item.status === "mismatch" && item.observedAddress && (
                  <p>
                    Carrier address:{" "}
                    {[
                      item.observedAddress.line1,
                      item.observedAddress.line2,
                      item.observedAddress.city,
                      item.observedAddress.region,
                      item.observedAddress.postalCode,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                )}
              </div>
            ))}
          </div>
        </>
      )}
      <p>
        Phone-system emergency calling has not been verified. A matching carrier
        address alone does not release device connection details.
      </p>
    </details>
  );
}
